import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDown, Plus } from "lucide-react";
import HeroCanvas from "@/components/HeroCanvas";
import useLenis from "@/hooks/useLenis";

// Free form endpoint (Formspree, Tally, Basin…). Set REACT_APP_FORM_ENDPOINT in .env
const FORM_ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT;
const BOOKING_URL = "https://calendly.com/yusufculha-ysfcagency";

const EASE = [0.16, 1, 0.3, 1];

const headlineLines = ["Quiet execution.", "Exceptional scale."];

const PILLARS = [
  {
    number: "01",
    title: "Monetization Architecture",
    description:
      "Private membership tiers, offer design, and brand-deal structures built around what your audience already trusts you for.",
    metric: "Deliverable: offer architecture and pricing model",
    id: "expertise-pillar-01",
  },
  {
    number: "02",
    title: "Operational Precision",
    description:
      "Backend infrastructure, fulfillment pipelines, and financial tracking — so the revenue side runs without you inside it.",
    metric: "Deliverable: a documented ops stack, run by systems",
    id: "expertise-pillar-02",
  },
  {
    number: "03",
    title: "Strategic Growth",
    description:
      "Audience retention, first-party channel migration, and a revenue mix that doesn't depend on one platform's algorithm.",
    metric: "Deliverable: owned-audience infrastructure and retention model",
    id: "expertise-pillar-03",
  },
];

const PHASES = [
  {
    span: "Weeks 01 — 02",
    title: "Audit",
    text: "I map what you already have: audience composition, what they came to you for, what they have paid for, and where attention leaks without converting. The output is a written diagnosis, not a pitch.",
    id: "engagement-phase-01",
  },
  {
    span: "Weeks 03 — 06",
    title: "Architecture",
    text: "We design one offer — the one your audience is already asking for without saying it. Pricing, tier structure, delivery model, and the systems that fulfill it.",
    id: "engagement-phase-02",
  },
  {
    span: "Weeks 07 — 10",
    title: "Build",
    text: "The backend goes in: payment, onboarding, fulfillment, retention triggers, reporting. Built to run without daily input from you.",
    id: "engagement-phase-03",
  },
  {
    span: "Weeks 11 — 12",
    title: "Handoff",
    text: "You get the documented system and the rules that operate it. I stay on for one full cycle to fix whatever reality breaks.",
    id: "engagement-phase-04",
  },
];

const POSITIONS = [
  {
    claim: "Reach is not the constraint.",
    text: "Most creators with a monetization problem don't need a bigger audience. They need one offer the audience they already have is qualified to buy.",
    id: "position-01",
  },
  {
    claim: "The second sale is the business.",
    text: "The first sale is attention. Retention is margin. Most creator products are architected for launch day and nothing after it.",
    id: "position-02",
  },
  {
    claim: "If it needs you daily, it isn't a business.",
    text: "A revenue system that runs on the creator's calendar is a job with extra steps.",
    id: "position-03",
  },
];

const CHAPTERS = [
  {
    num: "I",
    title: "Privacy First",
    text: "Your metrics, contracts, and internal numbers stay confidential. The work shows up in your results, not in my marketing.",
    id: "philosophy-chapter-1",
  },
  {
    num: "II",
    title: "High-Margin Focus",
    text: "I reject vanity reach. Every initiative is benchmarked against real net margin and recurring equity you own.",
    id: "philosophy-chapter-2",
  },
  {
    num: "III",
    title: "Deliberately Small",
    text: "This is a solo practice. I take a small number of engagements at a time because the work is hands-on, not delegated. You work with the person doing the work.",
    id: "philosophy-chapter-3",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: EASE, delay: i * 0.1 },
  }),
};

function Nav({ onInquire }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -80 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      data-testid="nav-container"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: EASE, delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "bg-[#0d0d0d]/80 border-b border-[#262626]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => jump("hero-section")}
          className="font-serif-display text-xl tracking-wide text-[#f5f5f5] flex items-center gap-1.5"
        >
          YSFC<span className="text-[#666] font-light">Agency</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] ml-0.5" />
        </button>

        <div className="hidden md:flex items-center gap-10">
          <button data-testid="nav-link-expertise" className="nav-link font-mono-label uppercase" onClick={() => jump("expertise-section")}>Expertise</button>
          <button data-testid="nav-link-engagement" className="nav-link font-mono-label uppercase" onClick={() => jump("engagement-section")}>Method</button>
          <button data-testid="nav-link-philosophy" className="nav-link font-mono-label uppercase" onClick={() => jump("philosophy-section")}>Philosophy</button>
          <button data-testid="nav-link-contact" className="nav-link font-mono-label uppercase" onClick={() => jump("inquiry-section")}>Contact</button>
          <button
            data-testid="nav-inquire-cta"
            onClick={onInquire}
            className="btn-outline px-6 py-2.5 text-xs uppercase tracking-[0.15em] font-mono-label"
          >
            Inquire
          </button>
        </div>

        <button
          data-testid="nav-inquire-cta-mobile"
          onClick={onInquire}
          className="btn-outline md:hidden px-5 py-2 text-xs uppercase tracking-[0.15em] font-mono-label"
        >
          Inquire
        </button>
      </div>
    </motion.nav>
  );
}

function Hero({ onInquire }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -80 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero-section"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center pt-32 pb-24 overflow-hidden"
    >
      <HeroCanvas />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0d0d0d] z-[2]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="font-mono-label text-[#666] mb-10 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[#d4af37]" />
          Monetization &amp; growth architecture for independent creators
        </motion.p>

        <h1 data-testid="hero-headline" className="font-serif-display font-light tracking-tight leading-[1.02] text-6xl sm:text-7xl lg:text-8xl">
          {headlineLines.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.55 + i * 0.15 }}
              >
                {i === 1 ? (
                  <span className="italic text-[#d4af37]">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1 }}
          className="mt-10 max-w-xl text-lg text-[#a3a3a3] font-light leading-relaxed"
        >
          Backend systems and monetization models for creators who built an audience before they built a business.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.15 }}
          className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-6"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={onInquire}
            className="btn-solid px-8 py-4 text-xs uppercase tracking-[0.18em] font-mono-label"
          >
            Begin an inquiry
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("engagement-section")}
            className="group flex items-center gap-2 text-sm text-[#a3a3a3] font-light hover:text-[#f5f5f5] transition-colors duration-500"
          >
            See how I work
            <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform duration-500" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const words = [
    "Backend Architecture",
    "High-Margin Monetization",
    "Brand Equity",
    "Private Advisory",
    "Compliance & Scaling",
  ];
  return (
    <div data-testid="editorial-marquee-container" className="border-y border-[#262626] bg-[#141414] py-8 overflow-hidden relative z-10">
      <Marquee speed={28} gradient gradientColor="#141414" gradientWidth={120}>
        {words.map((w, i) => (
          <span key={i} className="marquee-item font-serif-display">
            {w}
            <span className="dot" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Section({ children, className = "" }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Expertise() {
  return (
    <section id="expertise-section" data-testid="expertise-section" className="relative z-10 py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Section className="mb-16 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <p className="font-mono-label text-[#666] mb-5">/ 01 — Core Expertise</p>
            <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-2xl">
              Three disciplines, engineered in the background.
            </h2>
          </div>
        </Section>
      </div>

      <motion.div
        variants={{ show: { transition: { staggerChildren: 0.12 } } }}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 border-y border-[#262626] divide-y md:divide-y-0 md:divide-x divide-[#262626]"
      >
        {PILLARS.map((p) => (
          <motion.div
            key={p.id}
            data-testid={p.id}
            variants={fadeUp}
            className="pillar p-10 lg:p-12 min-h-[380px] flex flex-col"
          >
            <span className="pillar-num font-serif-display text-5xl text-[#333] leading-none">{p.number}</span>
            <h3 className="mt-10 font-serif-display text-2xl text-[#f5f5f5]">{p.title}</h3>
            <p className="mt-5 text-sm text-[#a3a3a3] font-light leading-relaxed flex-1">{p.description}</p>
            <p className="mt-8 pt-6 border-t border-[#262626] font-mono-label text-[#d4af37] text-[0.7rem]">{p.metric}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function Engagement() {
  return (
    <section id="engagement-section" data-testid="engagement-section" className="relative z-10 py-28 sm:py-36 border-b border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Section className="mb-16 max-w-2xl">
          <p className="font-mono-label text-[#666] mb-5">/ 02 — The Engagement</p>
          <h2 className="font-serif-display font-light text-4xl sm:text-5xl leading-[1.05]">
            How the work <span className="italic text-[#d4af37]">actually runs.</span>
          </h2>
          <p className="mt-6 text-[#a3a3a3] font-light leading-relaxed">
            A single engagement runs twelve weeks, in four phases. Nothing starts until the first one is written down.
          </p>
        </Section>

        <motion.div
          variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="border-t border-[#262626]"
        >
          {PHASES.map((p) => (
            <motion.div
              key={p.id}
              data-testid={p.id}
              variants={fadeUp}
              className="pillar grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-8 items-baseline px-2 sm:px-6 py-8 border-b border-[#262626]"
            >
              <span className="sm:col-span-3 font-mono-label text-[#666] text-[0.7rem] uppercase tracking-[0.2em]">{p.span}</span>
              <span className="sm:col-span-3 font-serif-display text-3xl sm:text-4xl text-[#f5f5f5] leading-none">{p.title}</span>
              <p className="sm:col-span-6 text-sm text-[#a3a3a3] font-light leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function PointOfView() {
  return (
    <section id="position-section" data-testid="position-section" className="relative z-10 py-28 sm:py-36 border-b border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Section className="mb-16 max-w-3xl">
          <p className="font-mono-label text-[#666] mb-5">/ 03 — Point of View</p>
          <h2 className="font-serif-display font-light text-4xl sm:text-5xl leading-[1.05]">
            Where creator revenue <span className="italic text-[#d4af37]">actually breaks.</span>
          </h2>
        </Section>

        <motion.div
          variants={{ show: { transition: { staggerChildren: 0.12 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="max-w-4xl border-t border-[#262626]"
        >
          {POSITIONS.map((p) => (
            <motion.div
              key={p.id}
              data-testid={p.id}
              variants={fadeUp}
              className="py-10 border-b border-[#262626]"
            >
              <h3 className="font-serif-display font-light text-2xl sm:text-3xl leading-[1.2] text-[#f5f5f5]">
                {p.claim}
              </h3>
              <p className="mt-5 max-w-2xl text-[#a3a3a3] font-light leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Philosophy() {
  return (
    <section id="philosophy-section" data-testid="philosophy-section" className="relative z-10 py-28 sm:py-36 border-b border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Section>
          <p className="font-mono-label text-[#666] mb-10">/ 04 — Philosophy</p>
          <p data-testid="philosophy-statement" className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.25] max-w-4xl text-[#f5f5f5]">
            True scale happens in the quiet background. No loud public flexing —{" "}
            <span className="italic text-[#d4af37]">just surgical precision, absolute privacy, and unassailable backend systems.</span>
          </p>
        </Section>

        <motion.div
          variants={{ show: { transition: { staggerChildren: 0.12 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#262626] border border-[#262626]"
        >
          {CHAPTERS.map((c) => (
            <motion.div key={c.id} data-testid={c.id} variants={fadeUp} className="bg-[#0d0d0d] p-10 lg:p-12">
              <div className="flex items-baseline gap-4">
                <span className="font-serif-display text-[#d4af37] text-3xl italic">{c.num}</span>
                <span className="w-full h-px bg-[#262626]" />
              </div>
              <h3 className="mt-8 font-serif-display text-xl text-[#f5f5f5]">{c.title}</h3>
              <p className="mt-4 text-sm text-[#a3a3a3] font-light leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Inquiry() {
  const [form, setForm] = useState({
    creator_name: "",
    platform_handle: "",
    email: "",
    monthly_reach: "",
    primary_objective: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.creator_name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (!FORM_ENDPOINT) {
      toast.error("The form isn't connected yet. Please use the consultation link above.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(FORM_ENDPOINT, form, {
        headers: { Accept: "application/json" },
      });
      setDone(true);
      toast.success("Inquiry received. I'll be in touch privately.");
      setForm({ creator_name: "", platform_handle: "", email: "", monthly_reach: "", primary_objective: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry-section" data-testid="inquiry-section" className="relative z-10 py-28 sm:py-36 border-b border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <Section className="lg:col-span-5">
          <p className="font-mono-label text-[#666] mb-8">/ 05 — Contact</p>
          <h2 className="font-serif-display font-light text-5xl sm:text-6xl leading-[1.02] text-[#f5f5f5]">
            Begin a<br />conversation.
          </h2>
          <p className="mt-8 max-w-md text-[#a3a3a3] font-light leading-relaxed">
            Tell me what you have built and where the revenue stops. If it's a fit, we'll talk. If it isn't, I'll tell you that too.
          </p>
          <a
            data-testid="inquiry-booking-link"
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center gap-2 group text-[#f5f5f5] hover:text-[#d4af37] transition-colors duration-500"
          >
            <span className="font-mono-label uppercase text-xs tracking-[0.15em]">Reserve a consultation</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-500" />
          </a>
        </Section>

        <Section className="lg:col-span-7">
          {done ? (
            <div className="h-full min-h-[300px] flex flex-col justify-center border border-[#262626] p-12">
              <span className="font-serif-display text-[#d4af37] text-4xl italic">Received.</span>
              <p className="mt-4 text-[#a3a3a3] font-light max-w-sm">
                Your inquiry has come through. Expect a discreet reply within two business days.
              </p>
              <button
                data-testid="inquiry-reset-btn"
                onClick={() => setDone(false)}
                className="mt-8 self-start btn-outline px-6 py-3 text-xs uppercase tracking-[0.15em] font-mono-label"
              >
                Send another
              </button>
            </div>
          ) : (
            <form data-testid="inquiry-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
              <div>
                <label className="font-mono-label text-[#666] text-[0.65rem]">Full Name *</label>
                <input data-testid="inquiry-input-name" className="lux-input mt-2" value={form.creator_name} onChange={update("creator_name")} placeholder="Your name" />
              </div>
              <div>
                <label className="font-mono-label text-[#666] text-[0.65rem]">Platform Handle</label>
                <input data-testid="inquiry-input-handle" className="lux-input mt-2" value={form.platform_handle} onChange={update("platform_handle")} placeholder="@handle" />
              </div>
              <div>
                <label className="font-mono-label text-[#666] text-[0.65rem]">Email *</label>
                <input data-testid="inquiry-input-email" type="email" className="lux-input mt-2" value={form.email} onChange={update("email")} placeholder="you@domain.com" />
              </div>
              <div>
                <label className="font-mono-label text-[#666] text-[0.65rem]">Monthly Reach</label>
                <input data-testid="inquiry-input-reach" className="lux-input mt-2" value={form.monthly_reach} onChange={update("monthly_reach")} placeholder="e.g. 240K" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label text-[#666] text-[0.65rem]">Primary Objective</label>
                <input data-testid="inquiry-input-objective" className="lux-input mt-2" value={form.primary_objective} onChange={update("primary_objective")} placeholder="Monetization, scaling, backend operations…" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label text-[#666] text-[0.65rem]">Message</label>
                <textarea data-testid="inquiry-input-message" rows={3} className="lux-input mt-2" value={form.message} onChange={update("message")} placeholder="Tell me about your current operation." />
              </div>
              <div className="sm:col-span-2 pt-2">
                <button
                  data-testid="inquiry-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-solid w-full sm:w-auto px-10 py-4 text-xs uppercase tracking-[0.18em] font-mono-label disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {submitting ? "Sending…" : "Submit Inquiry"}
                  {!submitting && <Plus className="w-4 h-4" />}
                </button>
              </div>
            </form>
          )}
        </Section>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer data-testid="footer-container" className="relative z-10 py-16 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <div className="font-serif-display text-lg tracking-wide flex items-center gap-1.5">
          YSFC<span className="text-[#666] font-light">Agency</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] ml-0.5" />
        </div>
        <div className="flex flex-wrap gap-8 font-mono-label text-[0.7rem] uppercase text-[#666]">
          <span>Privacy Policy</span>
          <span>Terms of Advisory</span>
        </div>
        <p data-testid="footer-copyright" className="text-[0.7rem] text-[#666] font-light">
          © 2026 YSFC Agency. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  useLenis();

  const scrollToInquiry = () => {
    const el = document.getElementById("inquiry-section");
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -80 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="grain bg-[#0d0d0d] min-h-screen text-[#f5f5f5] relative">
      <Nav onInquire={scrollToInquiry} />
      <Hero onInquire={scrollToInquiry} />
      <EditorialMarquee />
      <Expertise />
      <Engagement />
      <PointOfView />
      <Philosophy />
      <Inquiry />
      <Footer />
    </div>
  );
}
